import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Eye, Clock, ArrowRight, AlertCircle, Repeat, Layers, AlertTriangle } from "lucide-react";

/**
 * Insight Observation — calm, read-only operational learning surface.
 *
 * NOT analytics. NOT engagement. NOT a dashboard.
 * Just: what's being opened, which paths operators follow, where they
 * abandon, and which keys loop. Used to improve clarity of Help Mode.
 */

const WINDOWS = [
    { days: 1, label: "Last 24 hours" },
    { days: 7, label: "Last 7 days" },
    { days: 30, label: "Last 30 days" },
];

const fmtDuration = (ms) => {
    if (!ms || ms < 1000) return "—";
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    return `${m}m ${s % 60}s`;
};

const Section = ({ icon: Icon, title, subtitle, children, testId }) => (
    <section
        data-testid={testId}
        className="rounded-xl border border-slate-200/60 dark:border-slate-700/60 bg-white/60 dark:bg-slate-900/40 backdrop-blur-sm"
    >
        <header className="px-5 py-4 border-b border-slate-200/60 dark:border-slate-700/60 flex items-start gap-3">
            <div className="mt-0.5 text-slate-500 dark:text-slate-400">
                <Icon size={16} strokeWidth={1.75} />
            </div>
            <div>
                <h3 className="text-sm font-medium text-slate-800 dark:text-slate-100">
                    {title}
                </h3>
                {subtitle && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {subtitle}
                    </p>
                )}
            </div>
        </header>
        <div className="px-5 py-4">{children}</div>
    </section>
);

const EmptyRow = ({ text = "No observations in this window yet." }) => (
    <p className="text-xs italic text-slate-400 dark:text-slate-500">
        {text}
    </p>
);

const Stat = ({ label, value }) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {label}
        </span>
        <span className="text-base font-medium text-slate-800 dark:text-slate-100">
            {value}
        </span>
    </div>
);

const AdminInsightObservation = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [days, setDays] = useState(7);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        api.get(`/operational-base/insight/observation?days=${days}`)
            .then((res) => {
                if (cancelled) return;
                setData(res.data);
            })
            .catch((err) => {
                if (cancelled) return;
                if (err?.response?.status === 404) {
                    setError("This view is super-admin only.");
                } else {
                    setError("Could not load observations right now.");
                }
            })
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [days]);

    if (user?.role !== "super_admin") {
        return (
            <div className="p-8 text-sm text-slate-500" data-testid="insight-observation-forbidden">
                This view is restricted to super-admin.
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-6 lg:p-8 space-y-6" data-testid="insight-observation-page">
            <header className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        <Eye size={12} strokeWidth={1.75} />
                        <span>Help & Insights — Observation</span>
                    </div>
                    <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-50">
                        How operators are using Help Mode
                    </h1>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 max-w-2xl">
                        A quiet read-only view of what's been opened, which chip paths get used,
                        and where operators abandon. Used to find friction and improve clarity —
                        not engagement.
                    </p>
                </div>
                <div className="flex items-center gap-1 rounded-lg border border-slate-200/60 dark:border-slate-700/60 p-1 bg-white/60 dark:bg-slate-900/40">
                    {WINDOWS.map((w) => (
                        <button
                            key={w.days}
                            type="button"
                            onClick={() => setDays(w.days)}
                            data-testid={`insight-window-${w.days}`}
                            className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
                                days === w.days
                                    ? "bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900"
                                    : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800/60"
                            }`}
                        >
                            {w.label}
                        </button>
                    ))}
                </div>
            </header>

            {loading && (
                <div className="text-sm text-slate-500 dark:text-slate-400" data-testid="insight-observation-loading">
                    Reading observations…
                </div>
            )}

            {error && !loading && (
                <div className="text-sm text-rose-600 dark:text-rose-400" data-testid="insight-observation-error">
                    {error}
                </div>
            )}

            {!loading && !error && data && (
                <>
                    {/* Top-line stats */}
                    <section
                        className="grid grid-cols-2 sm:grid-cols-4 gap-6 rounded-xl border border-slate-200/60 dark:border-slate-700/60 bg-white/60 dark:bg-slate-900/40 backdrop-blur-sm px-5 py-4"
                        data-testid="insight-summary-strip"
                    >
                        <Stat label="Sessions" value={data.total_sessions ?? 0} />
                        <Stat label="Events" value={data.total_events ?? 0} />
                        <Stat
                            label="Avg session"
                            value={fmtDuration(data.average_session?.duration_ms)}
                        />
                        <Stat
                            label="Avg depth"
                            value={data.average_session?.depth?.toFixed?.(1) ?? data.average_session?.depth ?? 0}
                        />
                    </section>

                    <div className="grid lg:grid-cols-2 gap-6">
                        <Section
                            icon={Layers}
                            title="Most-opened entries"
                            subtitle="Which insight keys operators reach for. High counts on a key signal either real value or recurring confusion — read the abandons next to confirm which."
                            testId="insight-top-entries"
                        >
                            {data.top_entries?.length ? (
                                <ul className="divide-y divide-slate-200/60 dark:divide-slate-700/60">
                                    {data.top_entries.map((e) => (
                                        <li key={e.key} className="flex items-center justify-between py-2.5 text-sm" data-testid={`insight-top-${e.key}`}>
                                            <span className="font-mono text-[12px] text-slate-700 dark:text-slate-200 truncate pr-3">
                                                {e.key}
                                            </span>
                                            <span className="text-slate-500 dark:text-slate-400 tabular-nums">
                                                {e.opens}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : <EmptyRow />}
                        </Section>

                        <Section
                            icon={ArrowRight}
                            title={t('adminInsightObservation.commonChipPaths')}
                            subtitle="Where operators travel after opening an entry. Strong recurring hops suggest these relationships are operationally real."
                            testId="insight-common-paths"
                        >
                            {data.common_paths?.length ? (
                                <ul className="divide-y divide-slate-200/60 dark:divide-slate-700/60">
                                    {data.common_paths.map((p, i) => (
                                        <li key={`${p.from}-${p.to}-${i}`} className="flex items-center justify-between py-2.5 text-sm gap-3" data-testid={`insight-path-${i}`}>
                                            <span className="font-mono text-[12px] text-slate-700 dark:text-slate-200 truncate flex items-center gap-1.5 min-w-0">
                                                <span className="truncate">{p.from}</span>
                                                <ArrowRight size={11} className="text-slate-400 shrink-0" />
                                                <span className="truncate">{p.to}</span>
                                            </span>
                                            <span className="text-slate-500 dark:text-slate-400 tabular-nums shrink-0">
                                                {p.count}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : <EmptyRow />}
                        </Section>

                        <Section
                            icon={AlertCircle}
                            title="Abandoned without follow-up"
                            subtitle="Entries opened but never paired with a chip traversal in that session. May indicate the content stands on its own — or that operators didn't see a path forward."
                            testId="insight-abandoned"
                        >
                            {data.abandoned_entries?.length ? (
                                <ul className="divide-y divide-slate-200/60 dark:divide-slate-700/60">
                                    {data.abandoned_entries.map((e) => (
                                        <li key={e.key} className="flex items-center justify-between py-2.5 text-sm" data-testid={`insight-abandoned-${e.key}`}>
                                            <span className="font-mono text-[12px] text-slate-700 dark:text-slate-200 truncate pr-3">
                                                {e.key}
                                            </span>
                                            <span className="text-slate-500 dark:text-slate-400 tabular-nums">
                                                {e.count}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : <EmptyRow />}
                        </Section>

                        <Section
                            icon={Repeat}
                            title={t('adminInsightObservation.repeatedInSession')}
                            subtitle="Keys opened three or more times by the same operator in one session. Strong signal of unclear copy or missing context."
                            testId="insight-repeated-loops"
                        >
                            {data.repeated_loops?.length ? (
                                <ul className="divide-y divide-slate-200/60 dark:divide-slate-700/60">
                                    {data.repeated_loops.map((l) => (
                                        <li key={l.key} className="flex items-center justify-between py-2.5 text-sm" data-testid={`insight-loop-${l.key}`}>
                                            <span className="font-mono text-[12px] text-slate-700 dark:text-slate-200 truncate pr-3">
                                                {l.key}
                                            </span>
                                            <span className="text-slate-500 dark:text-slate-400 tabular-nums">
                                                {l.sessions} sessions
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : <EmptyRow />}
                        </Section>

                        <Section
                            icon={Clock}
                            title={t('adminInsightObservation.whereHelpOpens')}
                            subtitle="Routes operators are on when they invoke Help. Useful to confirm we're instrumenting the right surfaces next."
                            testId="insight-route-load"
                        >
                            {data.route_load?.length ? (
                                <ul className="divide-y divide-slate-200/60 dark:divide-slate-700/60">
                                    {data.route_load.map((r) => (
                                        <li key={r.route} className="flex items-center justify-between py-2.5 text-sm" data-testid={`insight-route-${r.route}`}>
                                            <span className="font-mono text-[12px] text-slate-700 dark:text-slate-200 truncate pr-3">
                                                {r.route}
                                            </span>
                                            <span className="text-slate-500 dark:text-slate-400 tabular-nums">
                                                {r.opens}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : <EmptyRow />}
                        </Section>

                        <Section
                            icon={Eye}
                            title={t('adminInsightObservation.viewportMix')}
                            subtitle="Bucketed only — mobile / tablet / desktop. Tells us whether to refine the rail's small-screen behaviour."
                            testId="insight-viewport-mix"
                        >
                            {data.viewport_mix && Object.keys(data.viewport_mix).length ? (
                                <ul className="space-y-2">
                                    {Object.entries(data.viewport_mix).map(([k, v]) => (
                                        <li key={k} className="flex items-center justify-between text-sm" data-testid={`insight-vp-${k}`}>
                                            <span className="capitalize text-slate-700 dark:text-slate-200">{k}</span>
                                            <span className="text-slate-500 dark:text-slate-400 tabular-nums">{v}%</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : <EmptyRow />}
                        </Section>
                    </div>

                    {/* ── Friction surface ──
                        "First few minutes of confusion" — sessions where the
                        operator tapped through ≥3 entries in ≤60s and didn't
                        resolve. Read these alongside the abandons + loops
                        above to see panic-tapping in full context. */}
                    {data.friction && (
                        <Section
                            icon={AlertTriangle}
                            title="Friction sessions"
                            subtitle={
                                `Sessions where an operator opened ${data.friction.rule.min_depth}+ entries in under ` +
                                `${Math.round(data.friction.rule.max_duration_ms / 1000)}s and closed without resolution. ` +
                                `These are the operators we're failing fastest — read the traversal chain to see ` +
                                `where the workflow graph stopped creating clarity.`
                            }
                            testId="insight-friction"
                        >
                            {data.friction.sessions?.length ? (
                                <div className="space-y-5">
                                    {/* Entry points + abandon points side-by-side */}
                                    <div className="grid sm:grid-cols-2 gap-5 pb-4 border-b border-slate-200/60 dark:border-slate-700/60">
                                        <div>
                                            <div className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                                                Where confusion starts
                                            </div>
                                            {data.friction.entry_points?.length ? (
                                                <ul className="space-y-1.5">
                                                    {data.friction.entry_points.map((e) => (
                                                        <li key={e.key} className="flex items-center justify-between text-sm" data-testid={`friction-entry-${e.key}`}>
                                                            <span className="font-mono text-[12px] text-slate-700 dark:text-slate-200 truncate pr-3">
                                                                {e.key}
                                                            </span>
                                                            <span className="text-slate-500 dark:text-slate-400 tabular-nums">
                                                                {e.sessions}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : <EmptyRow text="No entry-point pattern yet." />}
                                        </div>
                                        <div>
                                            <div className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                                                Where operators give up
                                            </div>
                                            {data.friction.abandon_points?.length ? (
                                                <ul className="space-y-1.5">
                                                    {data.friction.abandon_points.map((e) => (
                                                        <li key={e.key} className="flex items-center justify-between text-sm" data-testid={`friction-abandon-${e.key}`}>
                                                            <span className="font-mono text-[12px] text-slate-700 dark:text-slate-200 truncate pr-3">
                                                                {e.key}
                                                            </span>
                                                            <span className="text-slate-500 dark:text-slate-400 tabular-nums">
                                                                {e.sessions}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : <EmptyRow text="No abandon pattern yet." />}
                                        </div>
                                    </div>

                                    {/* Individual traversal chains */}
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                                            Recent traversal chains
                                        </div>
                                        <ul className="space-y-3">
                                            {data.friction.sessions.map((s) => (
                                                <li
                                                    key={s.session_id}
                                                    className="text-sm"
                                                    data-testid={`friction-session-${s.session_id}`}
                                                >
                                                    <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                                                        <span className="font-mono truncate pr-2">{s.route || "—"}</span>
                                                        <span className="tabular-nums shrink-0">
                                                            {fmtDuration(s.duration_ms)} · depth {s.depth}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-1.5 text-[12px] font-mono text-slate-700 dark:text-slate-200">
                                                        {s.chain.map((k, idx) => (
                                                            <span key={`${k}-${idx}`} className="flex items-center gap-1.5">
                                                                <span className={`px-2 py-0.5 rounded-md ${
                                                                    idx === 0
                                                                        ? "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"
                                                                        : idx === s.chain.length - 1
                                                                            ? "bg-rose-100 text-rose-900 dark:bg-rose-900/30 dark:text-rose-200"
                                                                            : "bg-slate-100 text-slate-700 dark:bg-slate-800/70 dark:text-slate-300"
                                                                }`}>
                                                                    {k}
                                                                </span>
                                                                {idx < s.chain.length - 1 && (
                                                                    <ArrowRight size={11} className="text-slate-400 shrink-0" />
                                                                )}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            ) : (
                                <EmptyRow text="No friction sessions in this window. Operators are reasoning their way through Help Mode at a comfortable pace." />
                            )}
                        </Section>
                    )}

                    <footer className="text-[11px] text-slate-400 dark:text-slate-500 pt-2 border-t border-slate-200/60 dark:border-slate-700/60">
                        Retention: {data.retention_days ?? 30} days rolling.
                        {data.telemetry_disabled ? " · Telemetry ingest currently disabled (env flag)." : ""}
                    </footer>
                </>
            )}
        </div>
    );
};

export default AdminInsightObservation;
